import loader from '@/lib/helpers/content/loader'
import logger from '@/lib/logger'

/**
 * Get a Page by route.
 *
 * Routes may be provided as an array of paths, like /about/me, an array of objects,
 * like { path: '/about/me', slug: 'me' }, or a mix of both.
 *
 * When passing strings, the last string after the `/` will be used as the slug, so `/about/me` will
 * become { me: {} }
 *
 * When more than one request is passed, only the meta from the first request will be used.
 *
 * @param {string[] | { path: string, slug: string }[]} requests an Array of routes
 * @param {boolean} useFetch If true, the mixin will use fetch() instead of asyncData(). Useful for rendering data outside of pages.
 */
export default (requests, useFetch = false) => {
  const asyncData = async ({ error }) => {
    try {
      const page = await loader(requests)

      return { asyncContentReady: true, page }
    } catch (e) {
      logger.error(e)

      error({ statusCode: 404, message: 'Page not found' })
    }
  }

  // Good ol' fashion function() so we have access to "this"
  const fetch = async function() {
    try {
      this.page = await loader(requests)
    } catch (e) {
      logger.error(e)

      this.$nuxt.error({ statusCode: 404, message: 'Page not found' })
    }
  }

  return {
    // Decide if we're using asyncData or fetch
    ...(useFetch ? { fetch } : { asyncData }),

    data: () => ({
      asyncContentReady: false,
      page: {},
    }),

    computed: {
      /**
       * Helper property to only show content when it's ready.
       */
      contentReady() {
        return useFetch ? !this.$fetchState.pending : this.asyncContentReady
      },
    },
  }
}
